import {ChangeDetectionStrategy, Component, Inject, Injectable, signal} from '@angular/core';

import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {AksjeeierEnkelModell, AksjeeierId, Aksjeeiertype, OppdaterAksjeeierSpesifikasjon, OpprettMedlemInvitasjonSpesifikasjon} from "../../../../../felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {AksjeeierDatatjeneste} from "../../../../../felles-ng2/tjenester/datalager/AksjeeierDatatjeneste";
import {FormsModule, ReactiveFormsModule} from "@angular/forms";
import {MatButtonModule} from "@angular/material/button";
import {MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef} from "@angular/material/dialog";
import {MatIconModule} from "@angular/material/icon";
import {MatToolbarModule} from "@angular/material/toolbar";
import {AksjeeierRedigerKomponent} from "../../komponenter/aksjeeier-rediger/aksjeeier-rediger";
import {VisSpinnerDirektiv} from "web-ng2/delt/direktiver/button--visSpinner/button--visSpinner";
import {trimTilNull} from "felles-ng2/støttefunksjoner/strenger";
import {TranslateModule} from "@ngx-translate/core";
import {skrivPlainDateSerialisertOpt} from "felles-ng2/støttefunksjoner/temporal";
import {VisDersomIkkeScrolletTilBunnDirektiv} from "web-ng2/delt/direktiver/visDersomIkkeScrolletTilBunn/visDersomIkkeScrolletTilBunn";

const debug = initDebugger('AksjeeierRedigerDialogKomponent');


@Component({
    selector: 'aksjeeier-rediger-dialog',
    templateUrl: './aksjeeier-rediger-dialog.html',
    styleUrls: ['./aksjeeier-rediger-dialog.scss'],
    imports: [
        FormsModule,
        MatButtonModule,
        MatDialogModule,
        MatIconModule,
        MatToolbarModule,
        AksjeeierRedigerKomponent,
        VisSpinnerDirektiv,
        ReactiveFormsModule,
        TranslateModule,
        VisDersomIkkeScrolletTilBunnDirektiv
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AksjeeierRedigerDialogKomponent {

    static vis(aksjeeier: Promise<AksjeeierEnkelModell>, dialog: MatDialog): MatDialogRef<AksjeeierRedigerDialogKomponent, void> {
        return dialog.open(AksjeeierRedigerDialogKomponent, {data: {aksjeeier: aksjeeier} as DialogParametre});
    }

    skjema = AksjeeierRedigerKomponent.opprettSkjema();
    lagrer = signal(false);

    constructor(@Inject(MAT_DIALOG_DATA) parametre: DialogParametre,
                public dialogRef: MatDialogRef<AksjeeierRedigerDialogKomponent>,
                private avhengigheter: AksjeeierRedigerDialogKomponentAvhengigheter) {
        debug("konstr", parametre)
        parametre.aksjeeier.then(aksjeeier => this.populerSkjema(aksjeeier));
    }

    avbryt() {
        this.dialogRef.close();
    }

    lagre() {
        this.lagrer.set(true);
        this.avhengigheter.oppdater(this.skjema.value.id!, this.modellSomSpesifikasjon())
          .then(() => this.dialogRef.close())
          .finally(() => this.lagrer.set(false));
    }

    //private hjelpemetoder

    private populerSkjema(aksjeeier: AksjeeierEnkelModell) {
        debug("initModell", aksjeeier)
        this.skjema.patchValue(AksjeeierRedigerKomponent.aksjeeierEnkelModellSomSkjemaVerdier(aksjeeier));
    }

    private modellSomSpesifikasjon(): OppdaterAksjeeierSpesifikasjon {
        return {
            type: this.skjema.value.type!,
            navn: this.skjema.value.navn!,
            adresse: this.skjema.value.adresse!,
            epost: trimTilNull(this.skjema.value.epost),
            foedselsdato: this.skjema.value.type == Aksjeeiertype.person ? skrivPlainDateSerialisertOpt(this.skjema.value.foedselsdato) : null,
            organisasjonsnummer: this.skjema.value.type == Aksjeeiertype.organisasjon ? trimTilNull(this.skjema.value.organisasjonsnummer) : null,
            representertAv: this.skjema.value.erNyInvitasjon ? null : (this.skjema.value.representertAv?.id ?? null),
            invitasjon: this.skjema.value.erNyInvitasjon ? this.somInvitasjonSpesifikasjon() : null
        };
    }

    private somInvitasjonSpesifikasjon(): OpprettMedlemInvitasjonSpesifikasjon | null {
        const invSkjema = this.skjema.controls.nyInvitasjonSkjema.getRawValue();
        if (!invSkjema.epost) return null;
        return {
            epost: invSkjema.epost,
            navn: trimTilNull(invSkjema.navn),
            mobilnummer: trimTilNull(invSkjema.mobilnummer),
            rolleId: invSkjema.rolle?.id ?? null,
            sendInvitasjon: this.skjema.value.sendInvitasjon ?? false
        };
    }
}

export interface DialogParametre {
    aksjeeier: Promise<AksjeeierEnkelModell>;
}

@Injectable()
export class AksjeeierRedigerDialogKomponentAvhengigheter {

    constructor(private aksjeeierDatatjeneste: AksjeeierDatatjeneste) {
    }

    oppdater(id: AksjeeierId, spesifikasjon: OppdaterAksjeeierSpesifikasjon): Promise<any> {
        return this.aksjeeierDatatjeneste.oppdater(id, spesifikasjon);
    }
}
